import { isValidElement, useCallback, useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';

const cx = (...c) => c.filter(Boolean).join(' ');
export { cx };

/* ── Button ──────────────────────────────────────────────────────────────
   variant: primary | secondary | ghost | danger | icon | link
   size: sm | md (default) | lg | xl   ·   to= renders a router Link */
export function Button({ variant = 'secondary', size, block, muted, bare, icon, iconSize = 20, to, className, children, type = 'button', ...rest }) {
    const cls = cx(
        'eg-btn',
        `eg-btn--${variant}`,
        size && size !== 'md' && `eg-btn--${size}`,
        block && 'eg-btn--block',
        muted && 'eg-btn--muted',
        bare && 'eg-btn--bare',
        className,
    );
    const content = <>{icon ? <Icon name={icon} size={iconSize} /> : null}{children}</>;
    if (to) return <Link to={to} className={cls} {...rest}>{content}</Link>;
    return <button type={type} className={cls} {...rest}>{content}</button>;
}

/* ── Segmented radiogroup ────────────────────────────────────────────── */
export function Segmented({ options, value, onChange, label, size, plain, className, disabled }) {
    const ref = useRef(null);
    const onKey = (e) => {
        const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
        if (!d) return;
        e.preventDefault();
        const i = options.findIndex((o) => o.value === value);
        const next = options[(i + d + options.length) % options.length];
        onChange(next.value);
        const btn = ref.current?.querySelectorAll('[role=radio]')[(i + d + options.length) % options.length];
        btn?.focus();
    };
    return (
        <div ref={ref} role="radiogroup" aria-label={label} className={cx('eg-seg', size && `eg-seg--${size}`, plain && 'eg-seg--plain', className)} onKeyDown={onKey}>
            {options.map((o) => {
                const on = o.value === value;
                return (
                    <button
                        key={String(o.value)}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        tabIndex={on ? 0 : -1}
                        disabled={disabled || o.disabled}
                        className="eg-seg__opt"
                        onClick={() => onChange(o.value)}
                    >
                        {o.swatch ? <span className="eg-seg__swatch" style={{ background: o.swatch }} /> : null}
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

export function Labeled({ label, children }) {
    return (
        <div className="eg-labeled">
            <span className="eg-caps-14">{label}</span>
            {children}
        </div>
    );
}

/* ── Check glyph, toggle chip, checkbox row ──────────────────────────── */
export function CheckGlyph({ on, failed, disabled }) {
    return (
        <span className={cx('eg-box', on && 'eg-box--on', failed && 'eg-box--failed', disabled && 'eg-box--disabled')} aria-hidden="true">
            {on ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="square"><path d="M5 12l5 5 9-11" /></svg> : null}
            {failed ? '!' : null}
        </span>
    );
}

export function ToggleChip({ on, failed, onToggle, children, title }) {
    return (
        <button
            type="button"
            className={cx('eg-chip', failed && 'eg-chip--failed')}
            aria-pressed={!!on && !failed}
            aria-disabled={failed || undefined}
            title={title}
            onClick={() => { if (!failed) onToggle(); }}
        >
            <CheckGlyph on={on && !failed} failed={failed} />
            <span>{children}</span>
        </button>
    );
}

export function CheckRow({ checked, disabled, onToggle, children, trailing }) {
    return (
        <button type="button" role="checkbox" aria-checked={!!checked} disabled={disabled} className="eg-check" onClick={onToggle}>
            <CheckGlyph on={checked} disabled={disabled} />
            <span className="eg-check__label">{children}</span>
            {trailing}
        </button>
    );
}

/* ── Status ──────────────────────────────────────────────────────────── */
export const STATUS = {
    complete: { text: 'Complete', tone: 'success', icon: 'check' },
    pending: { text: 'Pending', tone: 'warning', icon: 'clock' },
    incomplete: { text: 'Incomplete', tone: 'danger', icon: 'cross' },
    active: { text: 'Active', tone: 'success', icon: 'check' },
    verified: { text: 'Verified', tone: 'success', icon: 'check' },
    invited: { text: 'Invite sent', tone: 'warning', icon: 'mail' },
    unverified: { text: 'Awaiting you', tone: 'warning', icon: 'clock' },
    expired: { text: 'Expired', tone: 'danger', icon: 'clock' },
    inactive: { text: 'Inactive', tone: 'muted', icon: 'dash' },
    saved: { text: 'Saved', tone: 'success', icon: 'check' },
    failed: { text: 'Failed', tone: 'danger', icon: 'cross' },
    live: { text: 'Live now', tone: 'danger', icon: 'live' },
};

export function StatusBadge({ status, text, tone, icon, large, className }) {
    const s = STATUS[status] || {};
    const t = tone || s.tone || 'muted';
    return (
        <span className={cx('eg-status', `eg-status--${t}`, large && 'eg-status--lg', className)}>
            <Icon name={icon || s.icon || 'dash'} size={18} />
            <span>{text || s.text || status}</span>
        </span>
    );
}

/** Square dot + caps label, e.g. "● RECEIVING", optional pulse. */
export function DotLabel({ color, ink, pulse, children, large }) {
    return (
        <span className={cx('eg-status', large && 'eg-status--lg')} style={{ color: ink }}>
            <span className={cx('eg-dot', pulse && 'eg-dot--pulse')} style={{ background: color }} />
            <span>{children}</span>
        </span>
    );
}

export function Tag({ children, small, accent, className, style }) {
    return <span className={cx('eg-tag', small && 'eg-tag--sm', accent && 'eg-tag--accent', className)} style={style}>{children}</span>;
}

/* ── Banner ──────────────────────────────────────────────────────────── */
export function Banner({ tone = 'warning', title, children, action, pulse, role }) {
    return (
        <div role={role || (tone === 'danger' ? 'alert' : 'status')} className={cx('eg-banner', `eg-banner--${tone}`, pulse && 'eg-banner--top')}>
            {pulse ? <span className="eg-banner__pulse" /> : null}
            {tone === 'danger' && !pulse ? <Icon name="warning" size={20} color="var(--egdl-danger)" /> : null}
            <span className="eg-banner__text">
                {pulse && title ? <span className="eg-banner__title">{title}</span> : title ? <strong>{title} </strong> : null}
                {children}
            </span>
            {action}
        </div>
    );
}

/* ── Accordion ───────────────────────────────────────────────────────── */
export function Accordion({ title, summary, open, onToggle, children, label }) {
    const id = useId();
    return (
        <section aria-label={label || title} className="eg-accordion">
            <button type="button" className="eg-accordion__head" aria-expanded={open} aria-controls={id} onClick={onToggle}>
                <span className="eg-accordion__title">{title}</span>
                {summary ? <span className="eg-accordion__summary">{summary}</span> : null}
                <span className={cx('eg-chevron', open && 'eg-chevron--open')} />
            </button>
            {open ? <div id={id}>{children}</div> : null}
        </section>
    );
}

/**
 * What a grid or strip cell can always render: text, numbers and React elements as they are,
 * nothing for null / booleans, anything else (an object or array that came from a game) as
 * its JSON text. React throws on a plain object child, which would take the page down.
 */
export function cellContent(value) {
    if (value == null || typeof value === 'boolean') return null;
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (isValidElement(value)) return value;
    if (typeof value === 'bigint') return value.toString();
    try {
        const json = JSON.stringify(value);
        if (typeof json === 'string') return json;
    } catch { /* too deep, or a BigInt inside */ }
    return Object.prototype.toString.call(value);
}

const cellKey = (label, i) => (typeof label === 'string' || typeof label === 'number' ? label : `#${i}`);

export function KVGrid({ rows }) {
    return (
        <div className="eg-kv-grid">
            {rows.map((r, i) => (
                <div key={cellKey(r.label, i)} className="eg-kv">
                    <span className="eg-kv__k">{cellContent(r.label)}</span>
                    <span className="eg-kv__v">{cellContent(r.value)}</span>
                </div>
            ))}
        </div>
    );
}

/* ── Meta strip ──────────────────────────────────────────────────────── */
export function MetaStrip({ items }) {
    return (
        <div className="eg-meta">
            {items.filter(Boolean).map((m, i) => (
                <div key={cellKey(m.label, i)} className="eg-meta__item">
                    <span className="eg-meta__label">{cellContent(m.label)}</span>
                    <span className="eg-meta__value">{cellContent(m.value)}</span>
                </div>
            ))}
        </div>
    );
}

/* ── Facts: marking, spec rows, figures ──────────────────────────────────
   The shared "texture made of facts" vocabulary (launcher, game menus, portal). Every fact comes from
   data the page already has; an unknown one (null, undefined, '', false) is left out, never shown as a
   placeholder, and a piece with nothing left to show renders nothing. Tone-on-tone, never the accent. */
const known = (v) => v != null && v !== '' && v !== false;

/**
 * Marking — a caps name, a 16 × 2 dash, then 1–3 lines of facts.
 * lines: each a value, or an array of values joined by " / " ("Eleven Ball 1.0.99 / 12:04 / 19 Oct").
 */
export function Marking({ name, lines, className, ...rest }) {
    const shown = (lines || [])
        .map((l) => (Array.isArray(l) ? l.filter(known).map(String).join(' / ') : known(l) ? String(l) : ''))
        .filter(Boolean);
    if (!shown.length) return null;
    return (
        <div className={cx('eg-mark', className)} {...rest}>
            <span className="eg-mark__name">{name}</span>
            <span className="eg-mark__dash" aria-hidden="true" />
            {shown.map((l, i) => <span key={i} className="eg-mark__fact">{l}</span>)}
        </div>
    );
}

/**
 * SpecRows — label / value in two columns, one hairline above, a small gap between groups.
 * groups: [[{ label, value }, …], …]; rows without a value are left out, and so are empty groups.
 */
export function SpecRows({ groups, className }) {
    const shown = (groups || []).map((g) => (g || []).filter((r) => known(r.value))).filter((g) => g.length);
    if (!shown.length) return null;
    return (
        <dl className={cx('eg-spec', className)}>
            {shown.flatMap((g, gi) => g.map((r, ri) => {
                const lead = gi > 0 && ri === 0 ? ' eg-spec--group' : '';
                return [
                    <dt key={`k${gi}.${ri}`} className={'eg-spec__k' + lead}>{r.label}</dt>,
                    <dd key={`v${gi}.${ri}`} className={'eg-spec__v' + lead}>{cellContent(r.value)}</dd>,
                ];
            }))}
        </dl>
    );
}

/** Figures — one large number each, a caps label under it. items: [{ value, label }]. */
export function Figures({ items, className }) {
    const shown = (items || []).filter((f) => known(f.value));
    if (!shown.length) return null;
    return (
        <div className={cx('eg-figures', className)}>
            {shown.map((f) => (
                <div key={f.label} className="eg-figure">
                    <span className="eg-figure__value">{f.value}</span>
                    <span className="eg-figure__label">{f.label}</span>
                </div>
            ))}
        </div>
    );
}

/* ── Breadcrumb: items [{label, to?}] — last item is the current page ── */
export function Breadcrumb({ items }) {
    return (
        <nav aria-label="Breadcrumb" className="eg-crumbs">
            {items.map((it, i) => {
                const last = i === items.length - 1;
                return (
                    <span key={i} style={{ display: 'contents' }}>
                        {i > 0 ? <span className="eg-crumbs__sep" aria-hidden="true">/</span> : null}
                        {last ? (
                            <span aria-current="page" className="eg-crumbs__here">{it.label}</span>
                        ) : it.to ? (
                            <Link to={it.to} className="eg-crumbs__link">{it.label}</Link>
                        ) : it.onClick ? (
                            <button type="button" className="eg-crumbs__link" onClick={it.onClick}>{it.label}</button>
                        ) : (
                            <span className="eg-crumbs__mid">{it.label}</span>
                        )}
                    </span>
                );
            })}
        </nav>
    );
}

/* ── Tabs ────────────────────────────────────────────────────────────── */
export function Tabs({ tabs, value, onChange, label, rule, large }) {
    return (
        <div role="tablist" aria-label={label} className={cx('eg-tabs', rule && 'eg-tabs--rule', large && 'eg-tabs--lg')}>
            {tabs.map((t) => (
                <button key={t.value} type="button" role="tab" aria-selected={t.value === value} className="eg-tab" onClick={() => onChange(t.value)}>
                    {t.label}
                </button>
            ))}
        </div>
    );
}

/* ── Panel (LiveCard): bordered, 52 px caps header ───────────────────── */
export function Panel({ title, note, right, children, footer, className, style, bodyClassName, label }) {
    return (
        <section aria-label={label || (typeof title === 'string' ? title : undefined)} className={cx('eg-panel', className)} style={style}>
            {title ? (
                <header className="eg-panel__head">
                    <span className="eg-panel__title">{title}</span>
                    {note ? <span className="eg-panel__note">{note}</span> : null}
                    {right}
                </header>
            ) : null}
            {bodyClassName === undefined ? children : <div className={bodyClassName}>{children}</div>}
            {footer ? <footer className="eg-panel__foot">{footer}</footer> : null}
        </section>
    );
}

/* ── Empty / error / listening / loading ─────────────────────────────── */
/* details: facts under the title (a Marking or SpecRows), before the action. */
export function EmptyState({ title, children, tall, details, action }) {
    return (
        <div className={cx('eg-empty', tall && 'eg-empty--tall')}>
            {title ? <span className="eg-empty__title">{title}</span> : null}
            {children ? <span className="eg-empty__body">{children}</span> : null}
            {details}
            {action}
        </div>
    );
}

export function ListeningState({ title = 'Waiting for an active session…', children, details, action }) {
    return (
        <div role="status" className="eg-empty eg-empty--tall">
            <span className="eg-listening"><span className="eg-dot" /><span>Listening</span></span>
            <span className="eg-empty__title">{title}</span>
            {children ? <span className="eg-empty__body">{children}</span> : null}
            {details}
            {action}
        </div>
    );
}

export function ErrorState({ title, children, onRetry, retryLabel = 'Retry', kicker = 'Couldn’t load' }) {
    return (
        <div role="alert" className="eg-error">
            {kicker ? <span className="eg-error__kicker"><Icon name="warning" size={22} />{kicker}</span> : null}
            <span className="eg-error__title">{title}</span>
            {children ? <span className="eg-error__body">{children}</span> : null}
            {onRetry ? <Button variant="primary" onClick={onRetry}>{retryLabel}</Button> : null}
        </div>
    );
}

export function Skel({ w, h, raised, style }) {
    return <div className={cx('eg-skel', raised && 'eg-skel--raised')} style={{ width: w, height: h, ...style }} />;
}

/* ── Fields ──────────────────────────────────────────────────────────── */
export function TextField({ label, hint, error, id, className, inputClassName, ...input }) {
    const auto = useId();
    const fid = id || auto;
    return (
        <div className={cx('eg-field', className)}>
            {label ? <label htmlFor={fid} className="eg-field__label">{label}</label> : null}
            <input id={fid} className={cx('eg-input', inputClassName)} aria-invalid={error ? true : undefined} aria-describedby={error || hint ? fid + '-d' : undefined} {...input} />
            {error ? (
                <span id={fid + '-d'} className="eg-field__error"><Icon name="warning" size={16} />{error}</span>
            ) : hint ? (
                <span id={fid + '-d'} className="eg-field__hint">{hint}</span>
            ) : null}
        </div>
    );
}

export function SelectField({ label, value, onChange, options, small, className, id, ariaLabel, style }) {
    const auto = useId();
    const fid = id || auto;
    const select = (
        <span className={cx('eg-select', small && 'eg-select--sm', !label && className)} style={!label ? style : undefined}>
            <select id={fid} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label ? undefined : ariaLabel}>
                {options.map((o) => (
                    <option key={String(o.value)} value={o.value}>{o.label}</option>
                ))}
            </select>
        </span>
    );
    if (!label) return select;
    return (
        <div className={cx('eg-field', className)} style={style}>
            <label htmlFor={fid} className="eg-field__label">{label}</label>
            {select}
        </div>
    );
}

export function SearchField({ value, onChange, placeholder, label, style }) {
    return (
        <label className="eg-search" style={style}>
            <Icon name="search" size={20} />
            <input type="search" placeholder={placeholder} aria-label={label || placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
        </label>
    );
}

/* ── Meter / progress ────────────────────────────────────────────────── */
export function Meter({ value, mark, warning, label, valueText }) {
    const pct = Math.max(0, Math.min(100, value * 100));
    return (
        <div className="eg-meter" role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct)} aria-valuetext={valueText}>
            <div className={cx('eg-meter__fill', warning && 'eg-meter__fill--warning')} style={{ width: pct + '%' }} />
            {mark != null ? <div className="eg-meter__mark" style={{ left: Math.max(0, Math.min(100, mark * 100)) + '%' }} /> : null}
        </div>
    );
}

/* ── Slider (egdl-slider) — value in [min,max]; onCommit fires on release / key ── */
export function Slider({ value, min, max, step = 1, onChange, onCommit, label, disabled, ticks, rail }) {
    const ref = useRef(null);
    const clamp = useCallback((v) => {
        const s = Math.round((v - min) / step) * step + min;
        return Math.max(min, Math.min(max, +s.toFixed(6)));
    }, [min, max, step]);
    const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
    const onDown = (e) => {
        if (disabled) return;
        e.preventDefault();
        const r = ref.current.getBoundingClientRect();
        const at = (x) => clamp(min + Math.max(0, Math.min(1, (x - r.left) / r.width)) * (max - min));
        let v = at(e.clientX);
        onChange(v);
        const mv = (ev) => { v = at(ev.clientX); onChange(v); };
        const up = () => {
            window.removeEventListener('pointermove', mv);
            window.removeEventListener('pointerup', up);
            onCommit?.(v);
        };
        window.addEventListener('pointermove', mv);
        window.addEventListener('pointerup', up);
    };
    const onKey = (e) => {
        if (disabled) return;
        const big = (max - min) / 10;
        const d = { ArrowRight: step, ArrowUp: step, ArrowLeft: -step, ArrowDown: -step, PageUp: big, PageDown: -big }[e.key];
        let v;
        if (d) v = clamp(value + d);
        else if (e.key === 'Home') v = min;
        else if (e.key === 'End') v = max;
        else return;
        e.preventDefault();
        onChange(v);
        onCommit?.(v);
    };
    return (
        <div
            ref={ref}
            className="eg-slider"
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-label={label}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            aria-disabled={disabled || undefined}
            onPointerDown={onDown}
            onKeyDown={onKey}
        >
            {(ticks || []).map((t, i) => (
                <div key={i} className="eg-slider__tick" title={t.label} style={{ left: (((t.value - min) / (max - min)) * 100).toFixed(2) + '%', ...(t.color ? { background: t.color } : null) }} />
            ))}
            <div className="eg-slider__rail" style={rail ? { background: rail } : undefined}>
                <div className="eg-slider__fill" style={{ width: pct + '%' }} />
            </div>
            <div className="eg-slider__handle" style={{ left: pct + '%' }} />
        </div>
    );
}
